import type { MemberWithRatings } from "../../types";
import { compareSortValues, type GridSort, type GridSortValue } from "../gridSort";

export interface OrderedMemberRow {
  member: MemberWithRatings;
  depth: number;
}

interface Family {
  head: MemberWithRatings;
  dependents: MemberWithRatings[];
}

function byName(a: MemberWithRatings, b: MemberWithRatings): number {
  return a.lastName.localeCompare(b.lastName, undefined, { sensitivity: "base" }) || a.firstName.localeCompare(b.firstName, undefined, { sensitivity: "base" });
}

/** Real employees before Spock cohorts and Gradient Doe. */
function realFirst(a: MemberWithRatings, b: MemberWithRatings): number {
  return Number(a.memberType !== "Real") - Number(b.memberType !== "Real");
}

/** Each employee with their dependents; dependents without a subscriber become one-row families. */
function buildFamilies(members: readonly MemberWithRatings[]): Family[] {
  const placed = new Set<string>();
  const families: Family[] = [];
  for (const employee of members.filter((member) => member.relation === "Employee")) {
    const dependents = members.filter((member) => member.relation !== "Employee" && member.subscriberMemberId === employee.id);
    families.push({ head: employee, dependents });
    placed.add(employee.id);
    for (const dependent of dependents) placed.add(dependent.id);
  }
  for (const orphan of members.filter((member) => !placed.has(member.id))) families.push({ head: orphan, dependents: [] });
  return families;
}

function flatten(families: readonly Family[], sortDependents: (a: MemberWithRatings, b: MemberWithRatings) => number): OrderedMemberRow[] {
  return families.flatMap((family) => [{ member: family.head, depth: 0 }, ...[...family.dependents].sort(sortDependents).map((member) => ({ member, depth: 1 }))]);
}

/**
 * Default order: employees alphabetically by last name, each followed by their
 * dependents (also alphabetical); dependents without a subscriber trail at the
 * end. Synthetic members (Spock cohorts, Gradient Doe) sort after real employees.
 */
export function orderMembersBySubscriber(members: readonly MemberWithRatings[]): OrderedMemberRow[] {
  const families = buildFamilies(members).sort((a, b) => realFirst(a.head, b.head) || byName(a.head, b.head));
  return flatten(families, byName);
}

/**
 * Column sort that keeps the tree intact: families order by the employee's value
 * (ties alphabetical, real employees before synthetic ones when sorting by name),
 * and each family's dependents order by the same value beneath their subscriber.
 */
export function orderMembersByColumn(members: readonly MemberWithRatings[], sort: GridSort, sortValue: (member: MemberWithRatings) => GridSortValue): OrderedMemberRow[] {
  const factor = sort.direction === "asc" ? 1 : -1;
  const byColumn = (a: MemberWithRatings, b: MemberWithRatings) => compareSortValues(sortValue(a), sortValue(b)) * factor || byName(a, b);
  const byHead = sort.key === "name" ? (a: Family, b: Family) => realFirst(a.head, b.head) || byColumn(a.head, b.head) : (a: Family, b: Family) => byColumn(a.head, b.head);
  return flatten(buildFamilies(members).sort(byHead), byColumn);
}

export const LOCKED_TOOLTIP = "PreScreen is completed for this group. Reopen it to start a new version.";
export const SUPERSEDED_TOOLTIP = "A newer version of this group exists. Open it to make changes.";
